import { PaymentMethodPreset } from '@cpce/database/enums';
import z from 'zod';
import { FinancialAccountTypeSchema } from './financial-accounts.types.js';
export const ACCOUNTING_PLAN_PAYMENT_METHOD_COLUMN = 'Mode de règlement';
// Older files and the client's own wording: accepted as a header alias, never documented as such.
const ACCOUNTING_PLAN_PAYMENT_METHOD_COLUMN_ALIAS = 'Mode de paiement';
export const ACCOUNTING_PLAN_CSV_COLUMNS = [
    'Numéro',
    'Intitulé',
    'Type',
    ACCOUNTING_PLAN_PAYMENT_METHOD_COLUMN,
];
// Spellings accepted for a mode de règlement in the import file, compared after
// `normalizePaymentMethodLabel`: managers type what their software or habits give them ("Chèque",
// "CB", "Espèces"), not our enum.
export const PAYMENT_METHOD_PRESET_LABELS = {
    [PaymentMethodPreset.chequeEncaisse]: ['cheque', 'cheques', 'chq'],
    [PaymentMethodPreset.carteEncaissee]: [
        'cb',
        'carte',
        'cartes',
        'carte bancaire',
        'cartes bancaires',
        'carte bleue',
    ],
    [PaymentMethodPreset.especes]: ['especes', 'espece', 'cash', 'liquide', 'caisse'],
    [PaymentMethodPreset.decaissementBancaire]: [
        'banque',
        'compte bancaire',
        'virement',
        'virements',
        'prelevement',
        'decaissement',
    ],
    [PaymentMethodPreset.virementAEmettre]: [
        'virements a emettre',
        'virement a emettre',
        'virements emis',
        'sepa',
    ],
};
// The documented spelling of each preset, for messages and samples.
export const PAYMENT_METHOD_PRESET_CANONICAL_LABELS = {
    [PaymentMethodPreset.chequeEncaisse]: 'chèque',
    [PaymentMethodPreset.carteEncaissee]: 'CB',
    [PaymentMethodPreset.especes]: 'espèces',
    [PaymentMethodPreset.decaissementBancaire]: 'banque',
    [PaymentMethodPreset.virementAEmettre]: 'virements à émettre',
};
export const normalizePaymentMethodLabel = (value) => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
export const findPaymentMethodPreset = (label) => {
    const normalized = normalizePaymentMethodLabel(label);
    return Object.keys(PAYMENT_METHOD_PRESET_LABELS).find((preset) => PAYMENT_METHOD_PRESET_LABELS[preset].includes(normalized));
};
export const accountingPlanAccountCSVSchema = z.object({
    Numéro: z.string().trim().min(1, 'Le code est requis'),
    Intitulé: z.string().trim().min(1, 'Le nom est requis'),
    Type: FinancialAccountTypeSchema,
    [ACCOUNTING_PLAN_PAYMENT_METHOD_COLUMN]: z.string().trim().optional(),
    [ACCOUNTING_PLAN_PAYMENT_METHOD_COLUMN_ALIAS]: z.string().trim().optional(),
});
export const accountingPlanAccountSchema = z.object({
    code: z.string().trim().min(1, 'Le code est requis'),
    name: z.string().trim().min(1, 'Le nom est requis'),
    accountType: FinancialAccountTypeSchema,
    // Null means "not stated": an import never clears a mode de règlement already configured.
    paymentMethodPreset: z.enum(PaymentMethodPreset).nullable().default(null),
});
export const accountingPlanAccountCSVTransformSchema = accountingPlanAccountCSVSchema.transform((row, ctx) => {
    const label = row[ACCOUNTING_PLAN_PAYMENT_METHOD_COLUMN] ??
        row[ACCOUNTING_PLAN_PAYMENT_METHOD_COLUMN_ALIAS] ??
        '';
    const paymentMethodPreset = label === '' ? null : findPaymentMethodPreset(label);
    if (paymentMethodPreset === undefined) {
        const accepted = Object.values(PAYMENT_METHOD_PRESET_CANONICAL_LABELS).join(', ');
        ctx.addIssue({
            code: 'custom',
            path: [ACCOUNTING_PLAN_PAYMENT_METHOD_COLUMN],
            message: `Mode de règlement inconnu pour le compte ${row.Numéro} : « ${label} ». Valeurs acceptées : ${accepted}.`,
        });
        return z.NEVER;
    }
    return {
        code: row.Numéro,
        name: row.Intitulé,
        accountType: row.Type,
        paymentMethodPreset,
    };
});
export const accountingPlanDataSchema = z.object({
    accounts: z
        .array(accountingPlanAccountSchema)
        .min(1, 'Le plan comptable doit contenir au moins un compte'),
});
const accountingPlanPresetSchema = z.enum(PaymentMethodPreset).nullable();
export const accountingPlanImportUpdateSchema = z.object({
    code: z.string(),
    before: z.object({
        name: z.string().nullable(),
        accountType: FinancialAccountTypeSchema,
        paymentMethodPreset: accountingPlanPresetSchema,
    }),
    after: z.object({
        name: z.string(),
        accountType: FinancialAccountTypeSchema,
        paymentMethodPreset: accountingPlanPresetSchema,
    }),
});
export const accountingPlanImportPreviewSchema = z.object({
    toCreate: z.number().int().nonnegative(),
    toUpdate: z.array(accountingPlanImportUpdateSchema),
    unchanged: z.number().int().nonnegative(),
});
